import { WebSocketMessage } from '../types/models';
import { getWebSocketURL } from '../config/api-config';

type MessageHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private connectionPromise: Promise<void> | null = null;
  private _isConnected: boolean = false;
  private connectionStatusSubscribers: ((isConnected: boolean) => void)[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private userIdForReconnect: number | null = null;
  private currentReject: ((reason?: any) => void) | null = null; // Добавлено новое поле

  private notifyConnectionStatusSubscribers(): void {
    this.connectionStatusSubscribers.forEach(callback => {
      try {
        callback(this._isConnected);
      } catch (error) {
        console.error("Error in connection status subscriber:", error);
      }
    });
  }

  public subscribeToConnectionStatus(callback: (isConnected: boolean) => void): () => void {
    this.connectionStatusSubscribers.push(callback);
    try {
      callback(this._isConnected);
    } catch (error) {
      console.error("Error in initial callback for connection status subscriber:", error);
    }
    return () => {
      this.connectionStatusSubscribers = this.connectionStatusSubscribers.filter(cb => cb !== callback);
    };
  }

  private handleSocketMessage = (event: MessageEvent) => {
    console.log('WebSocketService: Received raw message data:', event.data); // Added log
    
    // Проверяем, что данные не пустые и не undefined
    if (!event.data || event.data === 'undefined' || event.data === 'null') {
      console.warn('WebSocketService: Received invalid message data:', event.data);
      return;
    }
    
    try {
        const message = JSON.parse(event.data as string) as WebSocketMessage;
        this.notifyHandlers(message);
    } catch (error) {
        console.error('Error parsing WebSocket message:', error);
    }
  };

  connect(userId: number): Promise<void> {
    this.userIdForReconnect = userId;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
      console.log('Cleared pending reconnect attempt due to new connect call.');
    }

    // Закомментированы оптимизации для предотвращения повторных подключений
    // так как наблюдались случаи, когда формально соединение было активно (readyState === OPEN),
    // но события от сервера не приходили. Принудительное пересоздание соединения решает эту проблему.
    // if (this.socket && this.socket.readyState === WebSocket.OPEN && this._isConnected) {
    //   console.log('WebSocket is already connected and open.');
    //   this.reconnectAttempts = 0;
    //   return Promise.resolve();
    // }

    // if (this.connectionPromise) {
    //   console.log('WebSocket connection attempt already in progress, returning existing promise.');
    //   return this.connectionPromise;
    // }

    console.log(`Attempting to establish new WebSocket connection for user ${userId}.`);

    if (this.socket) {
      console.log(`Cleaning up previous socket (readyState: ${this.socket.readyState}) before creating a new one.`);
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close(1000, "Client initiated new connection");
      }
      this.socket = null;
    }
    this._isConnected = false; // Сбрасываем, так как начинаем новое подключение

    this.connectionPromise = new Promise((resolve, reject) => {
      this.currentReject = reject; // Сохраняем функцию reject
      const wsUrl = getWebSocketURL(userId);
      console.log(`Connecting to WebSocket URL: ${wsUrl}`);
      const newSocket = new WebSocket(wsUrl);
      this.socket = newSocket;

      const promiseReference = this.connectionPromise!;
      let connectionTimeoutId: NodeJS.Timeout | null = null;
      const timeoutDuration = 15000; // 15 секунд

      const cleanupAndFinalize = (action: 'resolve' | 'reject', value?: any) => {
        if (connectionTimeoutId) {
          clearTimeout(connectionTimeoutId);
          connectionTimeoutId = null;
        }

        if (this.socket === newSocket) { // Снимаем слушатели только с "нашего" сокета
         newSocket.onopen = null;
         newSocket.onerror = null;
         newSocket.onclose = null;
       }

        if (this.connectionPromise === promiseReference) {
          this.connectionPromise = null;
          this.currentReject = null; // Обнуляем currentReject после завершения промиса
        }

        if (action === 'resolve') {
          resolve();
        } else {
          reject(value || new Error('WebSocket operation failed without specific error.'));
        }
      };

      connectionTimeoutId = setTimeout(() => {
        // Если таймаут сработал, значит onopen не был вызван вовремя.
        // Закрываем сокет, если он все еще в процессе подключения.
        // onclose затем вызовет cleanupAndFinalize с reject.
        // Если onclose не сработает, или для подстраховки, также вызываем reject.
        if (newSocket.readyState === WebSocket.CONNECTING) {
            console.warn(`WebSocket connection to ${newSocket.url} timed out after ${timeoutDuration / 1000}s. Closing socket.`);
            newSocket.close(4000, "Connection timeout by client"); // Используем допустимый код
        }
        // Промисы устойчивы к повторным вызовам resolve/reject. Первый вызов побеждает.
        cleanupAndFinalize('reject', new Error(`WebSocket connection timed out after ${timeoutDuration/1000}s.`));
      }, timeoutDuration);

      newSocket.onopen = () => {
        console.log(`WebSocket connection established to ${newSocket.url}.`);
        this._isConnected = true;
        this.notifyConnectionStatusSubscribers();
        this.reconnectAttempts = 0;
        cleanupAndFinalize('resolve');
      };

      newSocket.onmessage = this.handleSocketMessage;

      newSocket.onerror = (event) => {
        console.error(`WebSocket error event for ${newSocket.url}:`, event);
        // Ошибки на этапе подключения обычно приводят к onclose.
        // cleanupAndFinalize будет вызван из onclose или таймаута.
      };

      newSocket.onclose = (event) => {
        console.log(`WebSocket connection to ${newSocket.url} closed. Code: ${event.code}, Reason: '${event.reason || "No reason"}', WasConnectedInitially: ${this._isConnected}`);
        const wasOpenForThisSocket = this._isConnected; // Запоминаем, было ли _isConnected=true до того, как мы его сбросим

        // Промис должен быть отклонен, если onopen не был вызван.
        cleanupAndFinalize('reject', new Error(`WebSocket closed. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}.`));
        
        if (this.socket === newSocket) { // Убеждаемся, что это все еще "наш" сокет
            this._isConnected = false; // Теперь точно не подключены через этот сокет
            if (wasOpenForThisSocket) { // Если он был причиной _isConnected=true
                this.notifyConnectionStatusSubscribers();
            }
        }

        // Логика переподключения, только если это был "наш" сокет
        if (this.socket === newSocket) {
            if (event.code !== 1000 && event.code !== 1001 && event.code !== 1005 && this.userIdForReconnect !== null) {
                this.handleReconnect(this.userIdForReconnect);
            } else {
                console.log('WebSocket closed cleanly or by client, no automatic reconnect from this onclose.');
                if (event.code === 1000 || event.code === 1005) {
                    this.userIdForReconnect = null;
                    this.reconnectAttempts = 0;
                }
            }
        }
      };
    });
    return this.connectionPromise;
  }

  private handleReconnect(userId: number): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const timeout = Math.pow(2, this.reconnectAttempts - 1) * 1000;
      console.log(`WebSocket: Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${timeout / 1000}s...`);
      this.reconnectTimeoutId = setTimeout(() => {
        this.reconnectTimeoutId = null;
        if (this.userIdForReconnect === null) {
          console.log("WebSocket: Reconnect cancelled, userIdForReconnect is null (likely disconnected).");
          return;
        }
        console.log(`WebSocket: Executing reconnect attempt ${this.reconnectAttempts} for user ${userId}`);
        this.connect(userId).catch(err => {
          console.error(`WebSocket: Reconnect attempt ${this.reconnectAttempts} failed:`, err.message);
        });
      }, Math.min(timeout, 30000));
    } else {
      console.error(`WebSocket: Max reconnect attempts (${this.maxReconnectAttempts}) reached. Giving up.`);
      this.reconnectAttempts = 0;
      this.userIdForReconnect = null;
      if (this._isConnected) {
        this._isConnected = false;
        this.notifyConnectionStatusSubscribers();
      }
    }
  }

  disconnect(): void {
    console.log('WebSocket disconnect called.');
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
      console.log('Cleared pending reconnect attempt due to disconnect call.');
    }
    this.reconnectAttempts = 0;
    this.userIdForReconnect = null;

    if (this.socket) {
      console.log(`WebSocket: Closing socket (readyState: ${this.socket.readyState}) due to disconnect call.`);
      // Важно: перед закрытием сокета, обнуляем connectionPromise, если он относится к этому сокету.
      // Но disconnect может быть вызван, когда connectionPromise уже разрешен или отклонен.
      // cleanupAndFinalize в onclose сокета должен корректно обработать это.
      this.socket.close(1000, "Client disconnected");
    } else {
      console.log("WebSocket: disconnect called but no active socket.");
    }
    
    // Если connectionPromise все еще существует (например, таймаут еще не сработал, а мы вызвали disconnect)
    // его нужно принудительно отклонить.
    if (this.connectionPromise && this.currentReject) { // Добавлена проверка currentReject
        console.log("Disconnect called while connection promise was active. Attempting to reject it.");
        // Это может быть промис для сокета, который мы только что закрыли.
        // Его onclose должен вызвать cleanupAndFinalize.
        // Для подстраховки, если onclose не сработает или промис "завис":
        this.currentReject(new Error("WebSocket connection attempt cancelled by disconnect.")); // Используем сохраненную функцию reject
        this.connectionPromise = null; // Обнуляем ссылку
        this.currentReject = null; // Обнуляем currentReject
    } else if (this.connectionPromise) {
      // Если connectionPromise есть, но currentReject нет, значит промис уже разрешился/отклонился
      // или был создан без сохранения reject (что не должно происходить после изменений).
      // В этом случае просто обнуляем connectionPromise, чтобы избежать висячих ссылок.
      this.connectionPromise = null;
    }

    if (this._isConnected) {
      this._isConnected = false;
      this.notifyConnectionStatusSubscribers();
    }
  }

  public async sendMessage(messageData: object): Promise<void> {
    if (this.userIdForReconnect === null && (!this.socket || this.socket.readyState !== WebSocket.OPEN)) {
      console.error("WebSocket: Cannot send message. Connection not properly initiated or user ID not set.");
      throw new Error("WebSocket connection not properly initiated. Call connect(userId) first.");
    }

    if (!this.isConnected() || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.log('WebSocket: Not connected. Attempting to connect/await connection before sending message.');
      try {
        // Если есть активный промис подключения, ждем его.
        // Если нет, но был userIdForReconnect, значит connect вызывался, пытаемся подключиться.
        await (this.connectionPromise || this.connect(this.userIdForReconnect!));

        if (!this.isConnected() || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
          console.error('WebSocket: Connection failed or still not open after attempt for sendMessage.');
          throw new Error('WebSocket connection failed or not open after attempt.');
        }
      } catch (error) {
        console.error('WebSocket: Failed to connect/await connection before sending message:', error);
        throw error;
      }
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) { // Повторная проверка после await
      try {
        this.socket.send(JSON.stringify(messageData));
        console.log('WebSocket: Message sent:', messageData);
      } catch (error) {
        console.error('WebSocket: Error sending message:', error);
        throw error;
      }
    } else {
      console.error('WebSocket: Socket not open for sending after attempting to connect. State:', this.socket?.readyState);
      throw new Error('WebSocket is not open. Send failed.');
    }
  }

  public getSocket(): WebSocket | null {
    return this.socket;
  }

  public isConnected(): boolean {
    return this._isConnected && this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  addMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.push(handler);
  }

  removeMessageHandler(handler: MessageHandler): void {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }

  private notifyHandlers(message: WebSocketMessage): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in WebSocket message handler:', error);
      }
    });
  }
}

export const webSocketService = new WebSocketService();